import { useApolloClient } from '@apollo/client/react';
import { spacing } from '@moderneinc/design-system-tokens';
import { Box, ClickAwayListener, MenuList, Popper } from '@mui/material';
import { bindPopper, type PopupState } from 'material-ui-popup-state/hooks';
import { signOut } from 'next-auth/react';
import {
  type FunctionComponent,
  type MouseEventHandler,
  useContext,
  useEffect
} from 'react';
import { ACCOUNT_SETTINGS_PATH } from '../../../constants/general';
import { SIGN_OUT } from '../../../constants/messages';
import { CommandPaletteContext } from '../../../contexts/command-palette.context';
import { UserContext } from '../../../contexts/user.context';
import {
  MenuDivider,
  NavigationMenuItem,
  NavigationMenuItemButton
} from '../../account-menu/account-menu.styled';
import { UserCard } from '../../account-menu/user-card.component';
import { TopNavMenuPaper } from '../../navigation/top-nav-menu-paper.styled';
import { searchCommandShortcutSettings } from '../../settings/manage-appearance/keyboard-shortcuts-settings';
import { CenteredOpposingBoxes } from '../../styled-components/layouts/layouts.styled';
import { ThemeToggle } from '../../theme-toggle/theme-toggle.component';

type GlobalAccountMenuPopoverProps = {
  popoverState: PopupState;
};

export const GlobalActionButtonsAccountMenuPopover: FunctionComponent<
  GlobalAccountMenuPopoverProps
> = ({ popoverState }) => {
  const client = useApolloClient();
  const { togglePalette } = useContext(CommandPaletteContext);
  const {
    preferences: { commandMenuShortcut }
  } = useContext(UserContext);

  useEffect(() => {
    if (popoverState.isOpen) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
    }
  }, [popoverState.isOpen]);

  const handleClickAway = (_event: MouseEvent | TouchEvent) => {
    // Don't prevent default or stop propagation - let the click continue
    popoverState.close();
  };

  const handleLogout: MouseEventHandler<HTMLButtonElement> = async () => {
    await client.clearStore();
    window.location.href = '/api/auth/keycloak/logout';
    await signOut({
      redirect: false
    });
  };

  const commandShortcut = searchCommandShortcutSettings[commandMenuShortcut];

  return (
    <Popper
      {...bindPopper(popoverState)}
      sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
      placement="bottom-end"
      disablePortal={false}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        }
      ]}
    >
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onClick">
        <TopNavMenuPaper sx={{ pt: 1, pb: 2 }}>
          <UserCard />
          <MenuDivider />
          <MenuList sx={{ p: 0 }}>
            <NavigationMenuItem>
              <NavigationMenuItemButton
                data-testid="account-menu-settings-button"
                href={ACCOUNT_SETTINGS_PATH}
              >
                Account settings
              </NavigationMenuItemButton>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuItemButton
                data-testid="account-menu-command-button"
                onClick={togglePalette}
              >
                <CenteredOpposingBoxes>
                  <Box
                    sx={{
                      mr: 2
                    }}
                  >
                    Command menu
                  </Box>

                  {!commandShortcut.disabled && (
                    <Box
                      sx={{
                        fontSize: '.75rem'
                      }}
                    >
                      {commandShortcut.shortcut}
                    </Box>
                  )}
                </CenteredOpposingBoxes>
              </NavigationMenuItemButton>
            </NavigationMenuItem>
            <NavigationMenuItem>
              {/* A row (not a button): the segmented control owns interaction so
                  changing theme doesn't close the menu. */}
              <Box
                sx={{
                  // Fill the flex ModMenuItem so space-between separates the
                  // label from the toggle; gap guarantees a min separation.
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: `${spacing.spacing_2}px`,
                  paddingLeft: `${spacing.spacing_2}px`,
                  paddingRight: `${spacing.spacing_2}px`,
                  paddingTop: `${spacing.spacing_1_2}px`,
                  paddingBottom: `${spacing.spacing_1_2}px`
                }}
              >
                Theme
                <ThemeToggle />
              </Box>
            </NavigationMenuItem>
            <MenuDivider />
            <NavigationMenuItem>
              <NavigationMenuItemButton
                data-testid="account-menu-logout-button"
                aria-label={SIGN_OUT}
                onClick={handleLogout}
              >
                Sign out
              </NavigationMenuItemButton>
            </NavigationMenuItem>
          </MenuList>
        </TopNavMenuPaper>
      </ClickAwayListener>
    </Popper>
  );
};
